import {
  HubConnection,
  HubConnectionBuilder,
  IHttpConnectionOptions,
  LogLevel,
} from '@microsoft/signalr';
import { NEGOTIATING_SERVICE } from 'constants/signalrConstants';
import { createDevHeaders, resolveServiceUri } from 'helpers/http';
import { NotificationEvents } from 'types/pushNotifications';

function getNegotiatingServiceUri(): string {
  return resolveServiceUri(NEGOTIATING_SERVICE).replace(/\/$/, '');
}

/**
 * @param {String} hubName The service that the SignalR is negotiating the connection with.
 * @param {Function} authFunc The function by which the auth token can be acquired.
 * @returns {HubConnection} The connection to the SignalR hub.
 */
export const getNegotiatedConnection = (
  hubName: string,
  authFunc: () => string | Promise<string>
): HubConnection => {
  const authFuncString: () => Promise<string> = authFunc as () => Promise<string>;
  const connectionOptions: IHttpConnectionOptions = {
    accessTokenFactory: authFuncString,
    headers: createDevHeaders(),
  };

  return new HubConnectionBuilder()
    .withUrl(`${getNegotiatingServiceUri()}?hubName=${hubName}`, connectionOptions)
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        if (retryContext.elapsedMilliseconds < 10000) {
          // for the first 10 seconds, we want to retry every second
          return 1000;
        } else if (retryContext.elapsedMilliseconds < 60000) {
          // between 10-60 seconds, we want to retry every 5 seconds
          return 5000;
        } else {
          // If we've been reconnecting for more than 60 seconds
          // we will start backing off to 10 seconds per retry
          return 10000;
        }
      },
    })
    .withStatefulReconnect()
    .configureLogging(LogLevel.None)
    .build();
};

/**
 * @param {HubConnection} connection The function that will process the SignalR message
 * @param {String} target The type of SignalR message that is to be targeted.
 * @param {Function} processFunc The function that will process the SignalR message
 */
export const receiveNotifications = <T extends NotificationEvents>(
  connection: HubConnection,
  target: string,
  processFunc: (...msg: T[]) => void
) => {
  connection.off(target, processFunc);
  connection.on(target, processFunc);
};

/**
 * @param {HubConnection} connection The function that will process the SignalR message
 * @param {String} target The type of SignalR message that is to be targeted.
 */
export const stopNotifications = <T extends NotificationEvents>(
  connection: HubConnection,
  target: string,
  handler: (...msg: T[]) => void
) => {
  connection.off(target, handler);
};
